// Device Fingerprint Collection Utility
// Collects browser/device characteristics for security purposes

class DeviceFingerprint {
  constructor() {
    this.components = {};
  }

  // Main collection method
  async collect() {
    try {
      // Core components
      this.components.user_agent = navigator.userAgent;
      this.components.user_agent_hash = this.hashString(navigator.userAgent);
      this.components.platform = navigator.platform;
      this.components.language = navigator.language;
      this.components.languages = navigator.languages?.join(',') || navigator.language;
      this.components.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.components.timezone_offset = new Date().getTimezoneOffset();
      
      // Screen info
      this.components.screen_resolution = `${screen.width}x${screen.height}`;
      this.components.screen_available = `${screen.availWidth}x${screen.availHeight}`;
      this.components.color_depth = screen.colorDepth;
      this.components.pixel_ratio = window.devicePixelRatio || 1;
      
      // Hardware
      this.components.hardware_concurrency = navigator.hardwareConcurrency || 0;
      this.components.device_memory = navigator.deviceMemory || 0;
      this.components.max_touch_points = navigator.maxTouchPoints || 0;
      
      // Features
      this.components.cookies_enabled = navigator.cookieEnabled;
      this.components.do_not_track = navigator.doNotTrack || 'unspecified';
      this.components.online = navigator.onLine;
      
      // Canvas fingerprint
      this.components.canvas_hash = await this.getCanvasFingerprint();
      
      // WebGL fingerprint
      const webglData = this.getWebGLFingerprint();
      this.components.webgl_vendor = webglData.vendor;
      this.components.webgl_renderer = webglData.renderer;
      this.components.webgl_hash = webglData.hash;
      
      // Audio fingerprint
      this.components.audio_hash = await this.getAudioFingerprint();
      
      // Fonts (simplified - just check for common fonts)
      this.components.fonts_hash = await this.getFontsFingerprint();
      
      // Storage
      this.components.local_storage = this.hasLocalStorage();
      this.components.session_storage = this.hasSessionStorage();
      this.components.indexed_db = this.hasIndexedDB();
      
      // Generate unique device ID for this session
      this.components.session_device_id = this.generateSessionId();
      
      // Timestamp
      this.components.collected_at = new Date().toISOString();
      
      return this.components;
    } catch (error) {
      console.error('Error collecting fingerprint:', error);
      return this.components;
    }
  }

  // Canvas fingerprint
  async getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      
      // Draw text with various styles
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('WEQASA fingerprint', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('WEQASA fingerprint', 4, 17);
      
      // Add some shapes
      ctx.beginPath();
      ctx.arc(50, 25, 10, 0, Math.PI * 2);
      ctx.fillStyle = '#f00';
      ctx.fill();
      
      const dataUrl = canvas.toDataURL();
      return this.hashString(dataUrl);
    } catch (e) {
      return 'canvas_not_supported';
    }
  }

  // WebGL fingerprint
  getWebGLFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      
      if (!gl) {
        return { vendor: 'not_supported', renderer: 'not_supported', hash: 'webgl_not_supported' };
      }
      
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'unknown';
      const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown';
      
      // Get more WebGL params
      const params = [
        gl.getParameter(gl.VERSION),
        gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
        gl.getParameter(gl.MAX_TEXTURE_SIZE),
        gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
        vendor,
        renderer
      ].join('|');
      
      return {
        vendor,
        renderer,
        hash: this.hashString(params)
      };
    } catch (e) {
      return { vendor: 'error', renderer: 'error', hash: 'webgl_error' };
    }
  }

  // Audio fingerprint
  async getAudioFingerprint() {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        return 'audio_not_supported';
      }
      
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const analyser = context.createAnalyser();
      const gain = context.createGain();
      const scriptProcessor = context.createScriptProcessor(4096, 1, 1);
      
      gain.gain.value = 0; // Mute
      oscillator.type = 'triangle';
      oscillator.frequency.value = 10000;
      
      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gain);
      gain.connect(context.destination);
      
      oscillator.start(0);
      
      return new Promise((resolve) => {
        let resolved = false;
        
        scriptProcessor.onaudioprocess = (event) => {
          if (resolved) return;
          resolved = true;
          
          const output = event.inputBuffer.getChannelData(0);
          let sum = 0;
          for (let i = 0; i < output.length; i++) {
            sum += Math.abs(output[i]);
          }
          
          try {
            oscillator.disconnect();
            scriptProcessor.disconnect();
            gain.disconnect();
            if (context.state !== 'closed') {
              context.close();
            }
          } catch (e) {}
          
          resolve(this.hashString(sum.toString()));
        };
        
        // Timeout fallback
        setTimeout(() => {
          if (resolved) return;
          resolved = true;
          try {
            oscillator.disconnect();
            if (context.state !== 'closed') {
              context.close();
            }
          } catch (e) {}
          resolve('audio_timeout');
        }, 1000);
      });
    } catch (e) {
      return 'audio_error';
    }
  }

  // Fonts fingerprint (checks for common fonts)
  async getFontsFingerprint() {
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      'Arial', 'Arial Black', 'Calibri', 'Cambria', 'Comic Sans MS',
      'Courier New', 'Georgia', 'Helvetica', 'Impact', 'Lucida Console',
      'Palatino Linotype', 'Tahoma', 'Times New Roman', 'Trebuchet MS',
      'Verdana', 'Consolas', 'Monaco', 'Ubuntu', 'Roboto', 'Open Sans'
    ];
    
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const h = document.getElementsByTagName('body')[0];
    const s = document.createElement('span');
    s.style.fontSize = testSize;
    s.innerHTML = testString;
    
    const baseSizes = {};
    for (const baseFont of baseFonts) {
      s.style.fontFamily = baseFont;
      h.appendChild(s);
      baseSizes[baseFont] = { width: s.offsetWidth, height: s.offsetHeight };
      h.removeChild(s);
    }
    
    const detectedFonts = [];
    for (const font of testFonts) {
      let detected = false;
      for (const baseFont of baseFonts) {
        s.style.fontFamily = `'${font}', ${baseFont}`;
        h.appendChild(s);
        const matched = s.offsetWidth !== baseSizes[baseFont].width ||
                       s.offsetHeight !== baseSizes[baseFont].height;
        h.removeChild(s);
        if (matched) {
          detected = true;
          break;
        }
      }
      if (detected) {
        detectedFonts.push(font);
      }
    }
    
    return this.hashString(detectedFonts.join(','));
  }

  // Helper functions
  hasLocalStorage() {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch (e) {
      return false;
    }
  }

  hasSessionStorage() {
    try {
      sessionStorage.setItem('test', 'test');
      sessionStorage.removeItem('test');
      return true;
    } catch (e) {
      return false;
    }
  }

  hasIndexedDB() {
    return !!window.indexedDB;
  }

  hashString(str) {
    let hash = 0;
    if (!str || str.length === 0) return hash.toString(16);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  }

  generateSessionId() {
    return 'fp_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
}

// Create singleton instance
const deviceFingerprint = new DeviceFingerprint();

// Export for use in React
export const collectFingerprint = async () => {
  return await deviceFingerprint.collect();
};

export const getStoredDeviceId = () => {
  let deviceId = localStorage.getItem('weqasa_device_id');
  if (!deviceId) {
    deviceId = 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('weqasa_device_id', deviceId);
  }
  return deviceId;
};

export default DeviceFingerprint;
